import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DeepReadonlyObject } from 'rxdb';

import { ItineraryView, TripTypeEnum } from '@mopla/data-models';
import {
	ERideType,
	getDurationStr,
	getFromToString,
	getItineraryRideType,
	getItineraryTimingV2,
	getItineraryTransportTypes,
	getLegDuration,
	getTransportTypesText,
} from '@mopla/utils';

import { PriceBlock } from '../../molecules/PriceBlock';

import { PriceText } from './ui/PriceText';
import { RideHailingCardContent } from './ui/RideHailingCardContent';
import { ServiceCardLayout } from './ui/ServiceCardLayout';
import { deriveRideHailingViewModel } from './utils/deriveRideHailingViewModel';
import { renderMainTransportIcon } from './renderMainTransportIcon';

export interface IRideHailingServiceProps {
	itinerary: DeepReadonlyObject<ItineraryView>;
	onDetailsClick?: () => void;
	testId?: string;
	isSelected?: boolean;
	isSearching?: boolean;
	hasReservation?: boolean;
	priceNode?: ReactNode;
	showRideDescription?: boolean;
	timePrefix?: string;
}

export const RideHailingService: FC<IRideHailingServiceProps> = ({
	itinerary,
	onDetailsClick,
	priceNode,
	testId = 'ride-hailing-service',
	isSelected = false,
	isSearching = false,
	hasReservation = false,
	showRideDescription = false,
	timePrefix,
}) => {
	const { t } = useTranslation();
	const rideTypeKey = getItineraryRideType(itinerary);
	const isMixed =
		rideTypeKey === ERideType.MIXED || rideTypeKey === ERideType.MIXED_PUBLIC;
	const transportDescriptors = getItineraryTransportTypes(itinerary);

	const transportTypesText = isMixed
		? t('booking:travel_types.MIXED.title')
		: getTransportTypesText(transportDescriptors, t);

	const transportIconData = itinerary?.legs
		?.filter((leg) => leg.mode !== TripTypeEnum.WALK)
		.map((leg) => ({
			mode: leg.mode,
			duration: getLegDuration(leg),
			vehicleType: leg.vehicleType,
		}));

	const { duration } = getItineraryTimingV2(itinerary.legs);
	const durationStr = getDurationStr(t, duration, {
		withAround: true,
		exclude: ['seconds'],
	});

	const hasBus = transportDescriptors.some(
		(d) => d.transportType === TripTypeEnum.LBT
	);

	const timeStr = getFromToString(itinerary, t, {
		withAround: !hasBus && !hasReservation,
	});

	const firstTransport = transportIconData?.[0];
	const icon = firstTransport
		? renderMainTransportIcon(
				isMixed ? { mode: ERideType.MIXED } : firstTransport
		  )
		: null;

	const { labels, shouldShowLabels } = deriveRideHailingViewModel(itinerary);

	const baseTimeNode = hasReservation ? timeStr : durationStr;
	const timeNode = timePrefix ? `${timePrefix} ${baseTimeNode}` : baseTimeNode;

	/**
	 * 🚧WIP most likely we won't need this at all, bcs itinerary price better to be prepared outside of this component.
	 * The price depends on different conditions.
	 * */
	const priceContent =
		priceNode ??
		(itinerary ? (
			<PriceBlock
				legs={itinerary.legs}
				render={(children) => <PriceText>{children}</PriceText>}
				i18nVersion="V2"
			/>
		) : null);

	return (
		<ServiceCardLayout
			isSelected={isSelected}
			isSearching={isSearching}
			onClick={onDetailsClick}
			testId={testId}
		>
			<RideHailingCardContent
				shouldShowLabels={shouldShowLabels}
				icon={icon}
				transportTypesText={transportTypesText}
				timeNode={timeNode}
				hasReservation={hasReservation}
				labels={labels}
				testId={`${testId}-content`}
				priceNode={priceContent}
				rideDescriptionKey={rideTypeKey ?? undefined}
				showDescription={showRideDescription}
			/>
		</ServiceCardLayout>
	);
};
